import { HttpPortProtocol } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/network_pb";
import type { DisplayPort } from "utils/environment/networkAccessFallback";
import { getSSHPortId } from "components/NodeDetail/Environment/TabContent/Access/SSHAccess/addSSHAccessToEnvironmentRequest";
import {
  getIngressApplicationsFromEnvironment,
  isEnvironmentPastBuild,
} from "components/Environment/utils/environtmentUtils";
import type { Environment } from "components/Environment/utils/types";
import {
  getSecureLinkAccessAudience,
  userCanAccessSecureLink,
} from "components/Environment/utils/secureLinkAccess";
import type {
  EnvironmentApplicationAccess,
  EnvironmentApplicationAccessAudience,
} from "./EnvironmentApplicationChip";
import { WorkspaceStatus, type IApplication } from "models/Workspace.model";
import { userCanAccessApplicationPolicy } from "../../applicationAccess";

export const getCloudApplicationAccess = (
  application: IApplication,
  port: DisplayPort | undefined,
  userIdentities: readonly string[],
): EnvironmentApplicationAccess => {
  const userCanAccessApplication = userCanAccessApplicationPolicy(
    application,
    userIdentities,
  );
  const userCanAccessPort = port
    ? userCanAccessSecureLink(port, userIdentities)
    : true;

  if (!userCanAccessApplication || !userCanAccessPort) return "inaccessible";
  if (
    (application.policy?.allowEveryone ?? false) &&
    (port?.allowPublicUnauthenticated ?? true)
  ) {
    return "public";
  }
  return "authenticated";
};

const hasSpecifiedApplicationUsers = (application: IApplication) =>
  !(application.policy?.allowEveryone ?? false) &&
  (application.policy?.allowedUserAuthIDs ?? []).some(
    (identity) => identity.trim().length > 0,
  );

export const getCloudApplicationAccessAudience = (
  application: IApplication,
  port: DisplayPort | undefined,
): EnvironmentApplicationAccessAudience | undefined => {
  const portAudience = port ? getSecureLinkAccessAudience(port) : undefined;

  if (
    hasSpecifiedApplicationUsers(application) ||
    portAudience === "specified-users"
  ) {
    return "specified-users";
  }

  return portAudience === "any-authenticated" ? "any-authenticated" : undefined;
};

const BUILT_IN_SECURE_LINKS = [
  { prefix: "jupyter", label: "JupyterLab" },
  { prefix: "k8s-dashboard", label: "Kubernetes Dashboard" },
] as const;

const getHostnameName = (hostname: string | undefined) =>
  hostname?.split(".")[0]?.toLowerCase() ?? "";

const getDisplayHostnameName = (hostname: string | undefined) =>
  hostname?.split(".")[0] ?? "";

const stringPort = (port: number | string | undefined): string | undefined =>
  port == null ? undefined : String(port);

const stripKnownSuffix = (
  value: string,
  appIDCandidates: Array<string | undefined>,
) => {
  const ids = appIDCandidates.filter(
    (id): id is string => typeof id === "string" && id.length > 0,
  );
  const matchingID = ids.find((id) => value.endsWith(`-${id}`));
  return matchingID ? value.slice(0, -matchingID.length - 1) : value;
};

const getComparableHostnameName = (
  value: string | undefined,
  appIDCandidates: Array<string | undefined>,
) => stripKnownSuffix(getHostnameName(value), appIDCandidates);

const getComparableApplicationName = (
  value: string,
  appIDCandidates: Array<string | undefined>,
) => stripKnownSuffix(value.toLowerCase(), appIDCandidates);

const isReadySecureLinkPort = (port: DisplayPort) =>
  Boolean(port.hostname && !port.isEndpointPending);

const getCtaLabel = (application: IApplication) =>
  application.labels?.["cta-value"]?.trim();

const isCtaApplication = (application: IApplication) =>
  application.labels?.["cta-enabled"] === "true" ||
  Boolean(getCtaLabel(application));

const getCtaDisplayLabel = (application: IApplication) =>
  getCtaLabel(application) || application.name;

const getBuiltInSecureLinkLabel = (port: DisplayPort) => {
  const hostnameName = getHostnameName(port.hostname);
  return BUILT_IN_SECURE_LINKS.find(({ prefix }) =>
    hostnameName.startsWith(prefix),
  )?.label;
};

const getSecureLinkDisplayName = (port: DisplayPort) =>
  getBuiltInSecureLinkLabel(port) ||
  getDisplayHostnameName(port.hostname) ||
  `Port ${port.serverPort || port.portNumber}`;

const applicationMatchesPortByHostname = (
  application: IApplication,
  port: DisplayPort,
) => Boolean(application.hostname && application.hostname === port.hostname);

const applicationMatchesPortByName = (
  application: IApplication,
  port: DisplayPort,
  appIDCandidates: Array<string | undefined>,
) => {
  const secureLinkName = getComparableHostnameName(
    port.hostname,
    appIDCandidates,
  );
  if (!secureLinkName) return false;

  return (
    getComparableApplicationName(application.name, appIDCandidates) ===
      secureLinkName ||
    getComparableHostnameName(application.hostname, appIDCandidates) ===
      secureLinkName
  );
};

const applicationMatchesPortByPort = (
  application: IApplication,
  port: DisplayPort,
) => {
  const applicationPort = stringPort(application.port);
  return Boolean(
    applicationPort &&
    (applicationPort === stringPort(port.serverPort) ||
      applicationPort === stringPort(port.portNumber)),
  );
};

const findMatchingCtaApplication = (
  port: DisplayPort,
  ctaApplications: IApplication[],
  appIDCandidates: Array<string | undefined>,
) =>
  ctaApplications.find((application) =>
    applicationMatchesPortByHostname(application, port),
  ) ??
  ctaApplications.find((application) =>
    applicationMatchesPortByName(application, port, appIDCandidates),
  ) ??
  ctaApplications.find((application) =>
    applicationMatchesPortByPort(application, port),
  );

const serviceTypeFromHTTPPort = (port: DisplayPort) =>
  port.httpProtocol === HttpPortProtocol.HTTPS ? "HTTPS" : "HTTP";

const toApplicationFromSecureLinkPort = (
  port: DisplayPort,
  matchedCtaApplication: IApplication | undefined,
): IApplication => {
  const hostname = port.hostname ?? "";
  const ctaLabel = matchedCtaApplication
    ? getCtaDisplayLabel(matchedCtaApplication)
    : undefined;
  const label = ctaLabel || getSecureLinkDisplayName(port);
  const labels = matchedCtaApplication
    ? {
        ...matchedCtaApplication.labels,
        "cta-enabled": "true",
        "cta-value": ctaLabel || matchedCtaApplication.name,
      }
    : undefined;

  return {
    cloudflareApplicationID: port.portId,
    cloudflareDnsRecordID: "",
    hostname,
    name: label,
    serviceType: serviceTypeFromHTTPPort(port),
    port: port.serverPort || port.portNumber || 0,
    userApplicationSetupBash: "",
    policy: {
      allowedUserAuthIDs: [],
      allowEveryone: true,
      apiKey: {
        enabled: false,
        id: "",
        clientID: "",
        clientSecret: "",
      },
    },
    healthCheckID: "",
    labels,
  };
};

export const getCloudComputeCardApplicationsFromPorts = (
  environment: Environment,
  httpPorts: readonly DisplayPort[],
  applications: IApplication[],
): IApplication[] => {
  if (!isEnvironmentPastBuild(environment)) return [];

  const appIDCandidates = [environment.workspaceId, environment.environment_id];
  const ctaApplications = applications.filter(isCtaApplication);
  return httpPorts
    .filter(isReadySecureLinkPort)
    .map((port) =>
      toApplicationFromSecureLinkPort(
        port,
        findMatchingCtaApplication(port, ctaApplications, appIDCandidates),
      ),
    );
};

export const getCloudComputeCardApplications = (
  environment: Environment,
  httpPorts: readonly DisplayPort[] = [],
): IApplication[] => {
  const isPastBuild = isEnvironmentPastBuild(environment);
  const applications = getIngressApplicationsFromEnvironment(environment, {
    includeBuildFallback: !isPastBuild,
  });
  if (!isPastBuild || environment.labels?.["tunnel-provider"] !== "skybridge") {
    return applications;
  }

  return getCloudComputeCardApplicationsFromPorts(
    environment,
    httpPorts,
    applications,
  );
};

export const hasCloudComputeSshApplication = (
  tcpPorts: readonly DisplayPort[],
) => Boolean(getSSHPortId(tcpPorts));

export const shouldShowCloudComputeApplicationsLoading = (
  environment: Environment,
  isLoadingPorts: boolean,
) =>
  isLoadingPorts &&
  isEnvironmentPastBuild(environment) &&
  environment.labels?.["tunnel-provider"] === "skybridge";

export const isCloudComputeActiveState = (status: WorkspaceStatus): boolean =>
  status === WorkspaceStatus.Deploying ||
  status === WorkspaceStatus.Starting ||
  status === WorkspaceStatus.Running;

type CloudComputeInaccessibleAction = "Application" | "SSH";

export const getCloudComputeInactiveActionReason = (
  action: CloudComputeInaccessibleAction,
  statusLabel: string,
) =>
  action === "SSH"
    ? `SSH is unavailable when compute is ${statusLabel.toLowerCase()}`
    : `Link is unavailable when compute is ${statusLabel.toLowerCase()}`;

export const getCloudComputeActionDisabledReason = (
  status: WorkspaceStatus,
  action: CloudComputeInaccessibleAction = "Application",
  statusLabel: string = status,
): string | undefined =>
  isCloudComputeActiveState(status)
    ? undefined
    : getCloudComputeInactiveActionReason(action, statusLabel);

export const shouldShowCloudComputeStatusTooltip = isCloudComputeActiveState;

type CloudApplicationActionCandidate = {
  access: EnvironmentApplicationAccess;
  accessAudience?: EnvironmentApplicationAccessAudience;
  application: IApplication;
  hostname?: string;
};

export const getPrimaryCloudComputeApplicationAction = <
  T extends CloudApplicationActionCandidate,
>(
  candidates: readonly T[],
  isLoading: boolean,
): T | undefined => {
  if (isLoading) return undefined;

  const resolvedCandidates = candidates.filter(({ hostname }) =>
    Boolean(hostname),
  );

  return (
    resolvedCandidates.find(
      ({ access, application }) =>
        access !== "inaccessible" && isCtaApplication(application),
    ) ??
    resolvedCandidates.find(({ access }) => access !== "inaccessible") ??
    resolvedCandidates.find(({ application }) =>
      isCtaApplication(application),
    ) ??
    resolvedCandidates[0]
  );
};
